/**
 * Runtime overrides for the environment values compiled into the bundle.
 *
 * `ng build` swaps `environment.ts` for one of the per-configuration files (see
 * the fileReplacements in angular.json), which used to freeze the API and
 * Keycloak hostnames into the JS bundle — a domain change meant rebuilding and
 * republishing the frontend image, while every other component simply read its
 * hostnames from `deploy/.env`.
 *
 * The nginx image now writes `/env.js` on container start from its own
 * environment (see frontend/docker/40-render-env.sh), and index.html loads it
 * with a blocking <script> so `window.__env` is already populated when this
 * module is evaluated. Whatever it provides wins; whatever it omits falls back
 * to the compiled-in value, so `ng serve` and the committed defaults keep
 * working untouched and one image can now serve any domain.
 */

/** The subset of `environment` that `/env.js` may override. */
export interface RuntimeEnv {
  environmentLabel?: string;
  apiUrl?: string;
  auth?: {
    identityServerUrl?: string;
    clientId?: string;
    realm?: string;
  };
}

declare global {
  interface Window {
    __env?: RuntimeEnv;
  }
}

/** The shape `applyRuntimeOverrides` knows how to override. */
interface OverridableEnvironment {
  environmentLabel: string;
  apiUrl: string;
  auth: {
    identityServerUrl: string;
    clientId: string;
    realm: string;
  };
}

/**
 * An empty string is treated as "not configured" rather than as a value: the
 * entrypoint emits keys for variables that are set but blank, and a blank URL
 * or realm is never a deliberate choice — falling back to the compiled-in value
 * beats booting the app against `https://undefined`.
 */
const orFallback = (value: string | undefined, fallback: string): string =>
  value === undefined || value === '' ? fallback : value;

/**
 * `environmentLabel` is the one field where blank *is* a deliberate value (it
 * hides the environment banner), so here only an absent key falls back. The
 * entrypoint omits the key entirely when the variable is unset.
 */
const orFallbackAllowingBlank = (value: string | undefined, fallback: string): string =>
  value === undefined ? fallback : value;

export function applyRuntimeOverrides<T extends OverridableEnvironment>(base: T): T {
  const runtime: RuntimeEnv = (typeof window === 'undefined' ? undefined : window.__env) ?? {};

  return {
    ...base,
    environmentLabel: orFallbackAllowingBlank(runtime.environmentLabel, base.environmentLabel),
    apiUrl: orFallback(runtime.apiUrl, base.apiUrl),
    auth: {
      ...base.auth,
      identityServerUrl: orFallback(runtime.auth?.identityServerUrl, base.auth.identityServerUrl),
      clientId: orFallback(runtime.auth?.clientId, base.auth.clientId),
      realm: orFallback(runtime.auth?.realm, base.auth.realm),
    },
  };
}
